import { NIGERIAN_BENCHMARKS } from './constants';

export interface SolarSimulationState {
  solarPanelCapacityKw: number;
  batteryCapacityKwh: number;
  inverterCapacityKw: number;
  totalLoadWatts: number;
  peakSunHours: number;
  petrolPricePerLiter: number;
  dieselPricePerLiter: number;
  gridTariffPerKwh: number;
  generatorUsageHoursPerDay: number;
  timeOfDay?: number; // 0 to 24 hours
}

export interface SolarMetricsResult {
  dailyGenerationKwh: number;
  dailyConsumptionKwh: number;
  netEnergyBalanceKwh: number;
  batteryRuntimeHours: number;
  monthlyFuelSavingsNgn: number;
  monthlyGridSavingsNgn: number;
  totalMonthlySavingsNgn: number;
  annualSavingsNgn: number;
  co2OffsetKgPerYear: number;
  currentPowerGenerationKw: number;
  currentPowerConsumptionKw: number;
  systemEfficiencyPercent: number;
  generatorFuelSavedLitersMonthly: number;
}

export function calculateSolarMetrics(
  state: SolarSimulationState,
  currentSoc: number = 85
): SolarMetricsResult {
  const {
    solarPanelCapacityKw,
    batteryCapacityKwh,
    inverterCapacityKw,
    totalLoadWatts,
    peakSunHours,
    petrolPricePerLiter,
    gridTariffPerKwh,
    generatorUsageHoursPerDay,
    timeOfDay = 12,
  } = state;

  // Thermal derating (18% African thermal loss) & MPPT efficiency (95%)
  const mpptEfficiency = 0.95;
  const thermalDerating = 1 - NIGERIAN_BENCHMARKS.thermalDeratingFactor; // 0.82
  
  // Daily Generation = Capacity (kW) * Peak Sun Hours * Derating * MPPT
  const dailyGenerationKwh = solarPanelCapacityKw * peakSunHours * thermalDerating * mpptEfficiency;

  // Daily Consumption (kWh) = Total Load (kW) * 24h (or customized active hours)
  const loadKw = totalLoadWatts / 1000;
  const dailyConsumptionKwh = loadKw * 18; // assuming average active load duration

  const netEnergyBalanceKwh = dailyGenerationKwh - dailyConsumptionKwh;

  // Battery remaining hours at current load
  const usableBatteryKwh = batteryCapacityKwh * 0.8; // 80% DoD limit for LiFePO4 longevity
  const batteryRuntimeHours = loadKw > 0 ? (usableBatteryKwh / loadKw) : 24;

  // Financial ROI Math vs Generator & Grid
  // Generator fuel consumption per kWh ~ 0.45 Liters
  const monthlyGenKwh = loadKw * generatorUsageHoursPerDay * 30;
  const generatorFuelSavedLitersMonthly = monthlyGenKwh * NIGERIAN_BENCHMARKS.generatorFuelRateLitersPerKwh;
  const monthlyFuelSavingsNgn = generatorFuelSavedLitersMonthly * petrolPricePerLiter;

  // Grid savings (assuming erratic grid supplemented by solar)
  const monthlyGridKwh = Math.max(0, dailyConsumptionKwh * 30 - monthlyGenKwh);
  const monthlyGridSavingsNgn = monthlyGridKwh * (gridTariffPerKwh - 50); // net savings over erratic erratic supply

  const totalMonthlySavingsNgn = Math.round(monthlyFuelSavingsNgn + monthlyGridSavingsNgn);
  const annualSavingsNgn = totalMonthlySavingsNgn * 12;

  // CO2 offset: ~0.85 kg CO2 per kWh generated from fossil fuel generators in Nigeria
  const co2OffsetKgPerYear = Math.round(dailyGenerationKwh * 365 * 0.85);

  // Real-time instantaneous power estimation based on bell curve for time of day (6am to 6pm)
  let currentPowerGenerationKw = 0;
  if (timeOfDay >= 6 && timeOfDay <= 18) {
    const sunAngleFactor = Math.sin(((timeOfDay - 6) / 12) * Math.PI);
    currentPowerGenerationKw = Number((solarPanelCapacityKw * sunAngleFactor * thermalDerating * mpptEfficiency).toFixed(2));
  }

  const currentPowerConsumptionKw = Number(loadKw.toFixed(2));
  const systemEfficiencyPercent = Math.min(100, Math.round((currentPowerGenerationKw / Math.max(0.1, solarPanelCapacityKw)) * 100));

  return {
    dailyGenerationKwh: Number(dailyGenerationKwh.toFixed(2)),
    dailyConsumptionKwh: Number(dailyConsumptionKwh.toFixed(2)),
    netEnergyBalanceKwh: Number(netEnergyBalanceKwh.toFixed(2)),
    batteryRuntimeHours: Number(batteryRuntimeHours.toFixed(1)),
    monthlyFuelSavingsNgn,
    monthlyGridSavingsNgn,
    totalMonthlySavingsNgn,
    annualSavingsNgn,
    co2OffsetKgPerYear,
    currentPowerGenerationKw,
    currentPowerConsumptionKw,
    systemEfficiencyPercent,
    generatorFuelSavedLitersMonthly: Math.round(generatorFuelSavedLitersMonthly),
  };
}
